import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const StyledModal = styled(Modal)`
  .modal-content {
    background-color: #ffffff;
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    border-radius: ${(p) => p.theme.radius.md};
    border: none;
  }
  .modal-header {
    border-bottom: 1px solid ${(p) => p.theme.color.border};
    padding: calc(${(p) => p.theme.spacing.md} + 4px)
      ${(p) => p.theme.spacing.md} ${(p) => p.theme.spacing.md};
  }
  .modal-body {
    padding: ${(p) => p.theme.spacing.md};
    overflow: visible;
  }
  .modal-footer {
    border-top: 1px solid ${(p) => p.theme.color.border};
    padding: ${(p) => p.theme.spacing.sm} ${(p) => p.theme.spacing.md};
  }
`;

const ModalTitle = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  line-height: 1.2;
`;

const LeadText = styled.p`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
  line-height: 1.5;
  margin: 0 0 ${(p) => p.theme.spacing.md} 0;
`;

const InfoCard = styled.div`
  background: ${(p) => p.theme.color.surfaceAlt};
  border-radius: ${(p) => p.theme.radius.md};
  padding: ${(p) => p.theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.theme.spacing.sm};
`;

const FieldLabel = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Recipient = styled.span`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};
  color: ${(p) => p.theme.color.primary};
  word-break: break-word;
`;

/**
 * Offered when a job moves to Pending Pickup (#855).
 *
 * Two ways out, both of which move the job: the status change is the
 * driller's decision and is not up for reconsideration here -- only whether
 * the bowler hears about it. The note is appended to the standard template,
 * never sent on its own.
 */
const NotifyCustomerModal = ({
  show,
  onHide,
  onSkip,
  onSend,
  customerName,
  ballName,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");

  useEffect(() => {
    if (show) setNote("");
  }, [show]);

  return (
    <StyledModal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton>
        <ModalTitle>{t("jobBoard.notifyCustomerTitle")}</ModalTitle>
      </Modal.Header>
      <Modal.Body>
        <LeadText>{t("jobBoard.notifyCustomerBody")}</LeadText>
        <InfoCard>
          <div>
            <FieldLabel>{t("jobBoard.notifyCustomerRecipient")}</FieldLabel>
            <br />
            <Recipient>
              {customerName || "—"}
              {ballName ? ` · ${ballName}` : ""}
            </Recipient>
          </div>
          <div>
            <FieldLabel>{t("jobBoard.notifyCustomerNote")}</FieldLabel>
            <Form.Control
              as="textarea"
              rows={3}
              value={note}
              maxLength={2000}
              placeholder={t("jobBoard.notifyCustomerNotePlaceholder")}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </InfoCard>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onSkip}>
          {t("jobBoard.notifyCustomerSkip")}
        </Button>
        <Button variant="primary" onClick={() => onSend(note)}>
          {t("jobBoard.notifyCustomerSend")}
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default NotifyCustomerModal;
